import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import Isvg from 'react-inlinesvg';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';

import { PFE_ICONS } from 'constants/icons';
import URLS from 'constants/urls';

import { bem } from 'helpers/class-name';
import { httpClient } from 'helpers/http';
import { reportError } from 'helpers/error-logging';
import { makePath } from 'helpers/link-generation';

import Message from 'components/Message';
import { openDecagonChat } from 'components/Decagon';
import Button from 'components/Button';
import { Form, Input } from 'components/HookForm';

import { mfaFormMessages as messages } from './Auth.messages';
import { mfaVerificationSuccess } from './Auth.saga';
import './Auth.scss';

const { element } = bem('auth');

const OTP_CODE_LENGTH = 6;

function AutoSubmitInput({ otpCode, isSubmitting, hasError, onAutoSubmit, ...inputProps }) {
  useEffect(() => {
    if (otpCode?.length === OTP_CODE_LENGTH && !isSubmitting && !hasError) {
      onAutoSubmit();
    }
  }, [otpCode, isSubmitting, hasError, onAutoSubmit]);

  return <Input {...inputProps} />;
}

AutoSubmitInput.propTypes = {
  otpCode: PropTypes.string,
  isSubmitting: PropTypes.bool,
  hasError: PropTypes.bool,
  onAutoSubmit: PropTypes.func.isRequired,
};

function MfaVerification() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const intl = useIntl();

  const continuationToken = location.state?.mfaContinuationToken;

  useEffect(() => {
    if (!continuationToken) {
      navigate(makePath.login(), { replace: true, state: { error: 'mfa_token_missing' } });
    }
  }, [continuationToken, navigate]);

  const handleSubmit = async (values, { rejectSubmit }) => {
    try {
      const { data } = await httpClient.post(URLS.user.mfa.verifyMFAForLogin, {
        mfa_continuation_token: continuationToken,
        totp_code: values.OTPCode,
      });
      dispatch(mfaVerificationSuccess(data));
    } catch (error) {
      rejectSubmit(null, {
        OTPCode: intl.formatMessage(messages.codeNotValid),
      });
      reportError('MFA verification failed', error);
    }
  };

  const handleGoToRecoveryCode = () => {
    navigate(makePath.loginMFARecoveryCode(), { state: { mfaContinuationToken: continuationToken } });
  };

  return (
    <div className={element('wrapper')}>
      <Isvg className={element('logo')} src={PFE_ICONS.logotype} />

      <Message message={messages.title} as="h1" className={element('header')} />
      <Message message={messages.subtitle} as="p" className={element('hint')} />

      <Form defaultValues={{ OTPCode: '' }} useFormOverrides={{ mode: 'onChange' }} onSubmit={handleSubmit}>
        {({ formState: { errors, isSubmitting }, clearErrors, resetField, watch, submit }) => {
          const otpCode = watch('OTPCode');
          const hasError = !!errors.OTPCode;

          return (
            <AutoSubmitInput
              name="OTPCode"
              type="number"
              className={element('code-input', { loading: isSubmitting })}
              maxLength={OTP_CODE_LENGTH}
              disabled={isSubmitting}
              placeholder={messages.otpPlaceholder}
              required
              otpCode={otpCode}
              isSubmitting={isSubmitting}
              hasError={hasError}
              onAutoSubmit={submit}
              onFocus={() => {
                if (hasError) {
                  clearErrors('OTPCode');
                  resetField('OTPCode');
                }
              }}
            />
          );
        }}
      </Form>

      <Message
        message={messages.recoverHint}
        as="p"
        className={element('hint')}
        values={{
          button: text => (
            <Button styled="inline-link" onClick={handleGoToRecoveryCode} type="button">
              {text}
            </Button>
          ),
        }}
      />

      <div className={element('mfa-quick-link-container')}>
        <Message as={Link} className={element('quick-link')} message={messages.backToLogin} to={makePath.login()} />
        <Message
          as={Button}
          className={element('quick-link')}
          message={messages.support}
          onClick={openDecagonChat}
          styled="inline-link"
          type={Button.TYPES.BUTTON}
        />
      </div>
    </div>
  );
}

export default MfaVerification;
